import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components/macro';
import { themeGet } from '@styled-system/theme-get';
import {
  variant,
  border,
  typography,
  color,
  space,
  flexbox,
  compose,
} from 'styled-system';
import {
  createTransition,
  trackingId,
  disabled,
  useFocusVisibleIE,
  themeGetAsRem,
  themeGetResponsive,
  forcedColorsMedia,
  handleClick,
  isIE,
  useResponsiveCtx,
} from '@blueprint/ui';

// variants
const variants = {
  buttonVariant: variant({
    scale: 'buttons.variantMapping',
  }),
};

/**
 * Button
 */
const ButtonWrapper = styled.button`
  padding: 0;
  appearance: none;
  box-shadow: none;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  flex-shrink: 0;
  box-sizing: border-box;
  border-width: 1px;
  border-style: solid;
  overflow: hidden;
  border-radius: 0;
  user-select: none;
  vertical-align: middle;
  transition: ${createTransition(['background-color', 'color', 'border'])};

  &:disabled,
  &[aria-disabled='true'] {
    ${disabled({})}
    ${({ mode }) => themeGet(`buttons.disabled.${mode}`)};
  }

  ${({ size }) => themeGetAsRem(`buttons.sizeMapping.${size}`)};

  ${({ pureIcon }) =>
    isIE() &&
    !pureIcon &&
    css`
      &:after {
        content: '';
        min-height: inherit;
      }
    `};

  ${compose(typography, color, space, border, flexbox, variants.buttonVariant)}

  ${({ vMedia, hMedia, size }) => css`
    ${themeGetResponsive({
      vMedia,
      hMedia,
      vMediaStyles: css`
        ${themeGetAsRem('buttons.sizeMapping.small')};
      `,
    })};
    ${hMedia && themeGetAsRem(`buttons.hBreakpoints.sizeMapping.${size}`)};
  `};

  ${forcedColorsMedia()`
    &:disabled, &[aria-disabled="true"] {
      opacity: 1;
    }
  `}
`;

const Button = forwardRef(
  (
    {
      disabled = false,
      ariaDisabled = false,
      children,
      variant = 'light.primary',
      ...rest
    },
    ref,
  ) => {
    const isOnlyString =
      React.Children.count(children) === 1 && typeof children === 'string';
    const [mode] = variant.split('.');
    const { vMedia, hMedia } = useResponsiveCtx();
    const focusVisibleIE = useFocusVisibleIE(rest);
    const ariaDisabledProps = ariaDisabled
      ? {
          onClick: handleClick(null, true, true),
          onKeyDown: handleClick(null, true, true),
        }
      : {};

    return (
      <ButtonWrapper
        ref={ref}
        mode={mode}
        aria-disabled={ariaDisabled || disabled || null}
        disabled={ariaDisabled ? false : disabled}
        variant={variant}
        {...rest}
        {...ariaDisabledProps}
        {...focusVisibleIE}
        vMedia={vMedia}
        hMedia={hMedia}
      >
        {isOnlyString ? <span>{children}</span> : children}
      </ButtonWrapper>
    );
  },
);

Button.defaultProps = {
  variant: 'light.primary',
  size: 'large',
  disabled: false,
  type: 'button',
  'data-trackid': trackingId('Button'),
  'data-testid': 'Button',
};

Button.displayName = 'Button';

export default Button;
